import i18n from "i18next";
import { BaseService } from "../../core/baseService";
import { ApiResult } from "../../core/types";
import {
  LateCheckoutUpsellDto,
  CreateLateCheckoutUpsellDto,
  UpdateLateCheckoutUpsellDto,
  LateCheckoutUpsellAnalyticsDto,
  UpsellTargetDto,
} from "../types/lateCheckoutUpsell.types";

export interface LateCheckoutUpsellServiceInterface {
  getUpsells(): Promise<ApiResult<LateCheckoutUpsellDto[]>>;
  getUpsell(upsellId: string): Promise<ApiResult<LateCheckoutUpsellDto>>;
  createUpsell(upsell: CreateLateCheckoutUpsellDto): Promise<ApiResult<LateCheckoutUpsellDto>>;
  updateUpsell(upsellId: string, upsell: UpdateLateCheckoutUpsellDto): Promise<ApiResult<LateCheckoutUpsellDto>>;
  deleteUpsell(upsellId: string): Promise<ApiResult<void>>;
  getAnalytics(startDate?: string, endDate?: string): Promise<ApiResult<LateCheckoutUpsellAnalyticsDto>>;
  getAvailableTargets(upsellType: string): Promise<ApiResult<UpsellTargetDto[]>>;
}

export class LateCheckoutUpsellService
  extends BaseService
  implements LateCheckoutUpsellServiceInterface
{
  async getUpsells(): Promise<ApiResult<LateCheckoutUpsellDto[]>> {
    return this.handleRequest<LateCheckoutUpsellDto[]>(
      this.client.get("/api/admin/late-checkout-upsells", {
        headers: {
          "Accept-Language": i18n.language || "en-US",
        },
      })
    );
  }

  async getUpsell(upsellId: string): Promise<ApiResult<LateCheckoutUpsellDto>> {
    return this.handleRequest<LateCheckoutUpsellDto>(
      this.client.get(`/api/admin/late-checkout-upsells/${upsellId}`)
    );
  }

  async createUpsell(upsell: CreateLateCheckoutUpsellDto): Promise<ApiResult<LateCheckoutUpsellDto>> {
    return this.handleRequest<LateCheckoutUpsellDto>(
      this.client.post("/api/admin/late-checkout-upsells", upsell)
    );
  }

  async updateUpsell(
    upsellId: string,
    upsell: UpdateLateCheckoutUpsellDto
  ): Promise<ApiResult<LateCheckoutUpsellDto>> {
    return this.handleRequest<LateCheckoutUpsellDto>(
      this.client.put(`/api/admin/late-checkout-upsells/${upsellId}`, upsell)
    );
  }

  async deleteUpsell(upsellId: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/late-checkout-upsells/${upsellId}`)
    );
  }

  async getAnalytics(
    startDate?: string,
    endDate?: string
  ): Promise<ApiResult<LateCheckoutUpsellAnalyticsDto>> {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    const queryString = params.toString();
    const url = queryString
      ? `/api/admin/late-checkout-upsells/analytics?${queryString}`
      : "/api/admin/late-checkout-upsells/analytics";

    return this.handleRequest<LateCheckoutUpsellAnalyticsDto>(
      this.client.get(url)
    );
  }

  async getAvailableTargets(upsellType: string): Promise<ApiResult<UpsellTargetDto[]>> {
    return this.handleRequest<UpsellTargetDto[]>(
      this.client.get(`/api/admin/late-checkout-upsells/available-targets/${upsellType}`, {
        headers: {
          "Accept-Language": i18n.language || "en-US",
        },
      })
    );
  }
}
